import ButtonIcon from "components/ButtonIcon/ButtonIcon.tsx";
import { ControlledInput } from "components/Card/ControlledInput";
import { KaboodleToaster } from "components/common";
import { MaskedInput, Select, TextInput } from "components/inputs";
import Checkbox from "components/inputs/Checkbox/Checkbox";
import { Tooltip } from "components/Tooltip/Tooltip";
import { omit } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  treatmentAllocationAPI,
  usePatchTreatmentAllocationByIdMutation,
} from "services/msa-api";
import type { CPTCodeSelectOption } from "services/codes-api";
import {
  buildCPTCodeSelectedOption,
  mapCPTCodeResultToSelectOption,
  useGetCPTCodesByInputQuery,
} from "services/codes-api";
import { formatCPTCodeOptionLabel } from "utils/cptCodeOptionLabel";

import { getServiceField } from "../../../future-medical-utils";
import { useAllocationDragDrop } from "../../hooks/useAllocationDragDrop";
import { useAllocationUpdate } from "../../hooks/useAllocationUpdate";
import {
  ALLOCATION_DND_TYPES,
  OMIT_FIELDS,
  TIMING,
  TREATMENT_ALLOCATION_NAME_MAX_LENGTH,
} from "../../utils/allocation-constants";
import { sanitizeTreatmentAllocationData } from "../../utils/allocation-sanitization";
import { validateTreatmentField } from "../../utils/allocation-validation";
import { DropIndicator } from "../DropIndicator/DropIndicator";
import { TotalWithProbability } from "../TotalWithProbability";

import type { AppDispatch } from "services/store";
import type {
  AllocationRowWithSection,
  TreatmentAllocationRow as TreatmentAllocationRowType,
  TreatmentAllocationRowProps,
} from "../../utils/allocation-types";

type CPTCodeOption = CPTCodeSelectOption;

const MAX_CPT_SEARCH_RESULTS = 25;
type Props = TreatmentAllocationRowProps & {
  onDrop: (
    draggedRow: TreatmentAllocationRowProps,
    dropTargetRow: AllocationRowWithSection<TreatmentAllocationRowProps>,
  ) => void;
  totalRowsInSection: number;
};

/**
 * editable row for treatment allocations
 * supports exposure/mitigation tracking, seed payment checkbox, and surgical pricing
 * handles inline validation, debounced updates, and optimistic UI updates for checkboxes
 */
export const TreatmentAllocationRow = (props: Props) => {
  const { onDrop, totalRowsInSection, ...row } = props;
  const dispatch = useDispatch<AppDispatch>();
  const [patchTreatmentAllocationById] =
    usePatchTreatmentAllocationByIdMutation();
  const [cptSearchTerm, setCptSearchTerm] = useState<string>("");

  const { data: cptCodeResults, isFetching: isSearchingCptCodes } =
    useGetCPTCodesByInputQuery(
      {
        search: cptSearchTerm || undefined,
        limit: MAX_CPT_SEARCH_RESULTS,
      },
      { skip: !cptSearchTerm },
    );

  const { executeUpdate, isAnyRowUpdating } = useAllocationUpdate({
    rowId: row.id,
  });

  const {
    reset,
    control,
    formState: { errors, dirtyFields },
    getValues,
    setValue,
  } = useForm({
    defaultValues: row,
  });

  useEffect(() => {
    reset(row, {
      keepDirtyValues: true,
    });
  }, [reset, row.id, row.modified]);

  const currentCPTCode = row.cpt_codes?.[0] || null;

  const cptCodeOptions = useMemo<CPTCodeOption[]>(() => {
    if (!cptCodeResults?.results) return [];

    return cptCodeResults.results.map(mapCPTCodeResultToSelectOption);
  }, [cptCodeResults]);

  const currentCPTCodeOption = currentCPTCode
    ? buildCPTCodeSelectedOption(
        currentCPTCode.code,
        currentCPTCode.description || "",
        cptCodeOptions,
      )
    : null;

  const handleCPTCodeSelect = useCallback(
    async (option: CPTCodeOption | null) => {
      const selectedCodes = option
        ? [
            {
              code: option.code,
              description: option.description,
              price: 0,
            },
          ]
        : [];
      const currentName = getValues("name");
      const shouldSetNameFromCpt = !dirtyFields.name;
      const treatmentName = shouldSetNameFromCpt
        ? (option?.description || "").slice(
            0,
            TREATMENT_ALLOCATION_NAME_MAX_LENGTH,
          )
        : currentName;

      if (shouldSetNameFromCpt) {
        setValue("name", treatmentName);
      }

      const patchResult = dispatch(
        treatmentAllocationAPI.util.updateQueryData(
          "getTreatmentAllocation",
          { filters: getServiceField(row.service, true) },
          (draft) => {
            if (!draft?.results) return;
            const rowToUpdate = draft.results.find(
              (r: TreatmentAllocationRowType) => r.id === row.id,
            );
            if (rowToUpdate) {
              rowToUpdate.cpt_codes = selectedCodes;
              if (shouldSetNameFromCpt) {
                rowToUpdate.name = treatmentName;
              }
            }
          },
        ),
      );

      try {
        await patchTreatmentAllocationById({
          id: row.id,
          data: sanitizeTreatmentAllocationData(
            shouldSetNameFromCpt
              ? { name: treatmentName, cpt_codes: selectedCodes }
              : { cpt_codes: selectedCodes },
          ),
        }).unwrap();
      } catch (error) {
        console.error("Error updating CPT code:", error);
        patchResult.undo();
        KaboodleToaster.error("Failed to update CPT code");
      }
    },
    [
      dispatch,
      dirtyFields.name,
      getValues,
      patchTreatmentAllocationById,
      row.id,
      row.service,
    ],
  );

  const handleFieldSave = useCallback(
    async (
      fieldName: string,
      value: string | number | boolean,
      immediate = false,
    ) => {
      // validate the field before saving
      const validationError = validateTreatmentField(fieldName, value);
      if (validationError) {
        KaboodleToaster.error(validationError);
        return;
      }

      executeUpdate(
        async () => {
          try {
            const updatedData = {
              ...getValues(),
              [fieldName]: value,
            };
            const cleanedData = omit(updatedData, OMIT_FIELDS);
            const sanitizedData = sanitizeTreatmentAllocationData(cleanedData);

            await patchTreatmentAllocationById({
              id: row.id,
              data: sanitizedData,
            }).unwrap();
          } catch (error) {
            console.error("TreatmentAllocationRow", error);
            KaboodleToaster.error("Failed to update treatment allocation");
            throw error;
          }
        },
        {
          rowId: row.id,
          immediate,
          debounceMs: immediate ? TIMING.IMMEDIATE : TIMING.DEBOUNCE_MS,
          maxWaitMs: immediate ? TIMING.IMMEDIATE : TIMING.MAX_WAIT_MS,
        },
      );
    },
    [row.id, getValues, patchTreatmentAllocationById, executeUpdate],
  );

  const handleSeedCheckboxChange = (checked: boolean) => {
    // optimistic update the seed payment status
    dispatch(
      treatmentAllocationAPI.util.updateQueryData(
        "getTreatmentAllocation",
        { filters: getServiceField(row.service, true) },
        (draft) => {
          if (!draft?.results) return;
          const rowToUpdate = draft.results.find(
            (r: TreatmentAllocationRowType) => r.id === row.id,
          );
          if (rowToUpdate) {
            rowToUpdate.is_included_in_seed_payment = checked;
          }
        },
      ),
    );

    handleFieldSave("is_included_in_seed_payment", checked, true);
  };

  const handleExposureMitigationChange = (checked: boolean, field: string) => {
    // prepare the update data with default values when checking exposure/mitigation
    const updateData: Record<string, string | number | boolean> = {
      [field]: checked,
    };

    // when checking exposure for the first time, default the exposure fields to parent values
    if (field === "has_exposure" && checked && !row.has_exposure) {
      updateData.exposed_unit_price = row.unit_price ?? 0;
      updateData.exposed_units_per_year = row.units_per_year ?? 0;
      updateData.number_of_years_exposed = row.number_of_years ?? 0;

      // immediately update form state for instant visual feedback
      setValue("exposed_unit_price", row.unit_price ?? 0);
      setValue("exposed_units_per_year", row.units_per_year ?? 0);
      setValue("number_of_years_exposed", row.number_of_years ?? 0);
    }

    // when checking mitigation for the first time, default the mitigation fields to parent values
    if (field === "has_mitigation" && checked && !row.has_mitigation) {
      updateData.mitigated_unit_price = row.unit_price ?? 0;
      updateData.mitigated_units_per_year = row.units_per_year ?? 0;
      updateData.number_of_years_mitigated = row.number_of_years ?? 0;

      // immediately update form state for instant visual feedback
      setValue("mitigated_unit_price", row.unit_price ?? 0);
      setValue("mitigated_units_per_year", row.units_per_year ?? 0);
      setValue("number_of_years_mitigated", row.number_of_years ?? 0);
    }

    // optimistic update
    const patchResult = dispatch(
      treatmentAllocationAPI.util.updateQueryData(
        "getTreatmentAllocation",
        { filters: getServiceField(row.service, true) },
        (draft) => {
          if (!draft?.results) return;
          const rowToUpdate = draft.results.find(
            (r: TreatmentAllocationRowType) => r.id === row.id,
          );
          if (rowToUpdate) {
            Object.assign(rowToUpdate, updateData);
          }
        },
      ),
    );

    executeUpdate(
      async () => {
        try {
          // patch with all the update data
          const updatedData = {
            ...getValues(),
            ...updateData,
          };
          await patchTreatmentAllocationById({
            id: row.id,
            data: omit(updatedData, OMIT_FIELDS),
          }).unwrap();
        } catch (error) {
          patchResult.undo();
          KaboodleToaster.error(error);
          throw error;
        }
      },
      {
        rowId: row.id,
        immediate: true,
        debounceMs: 0,
        maxWaitMs: 0,
      },
    );
  };

  // use centralized drag-drop hook with dev branch logic
  const { drag, drop, isDragging, showDropIndicator, showIndicatorBelow } =
    useAllocationDragDrop({
      row: { ...row, ...getValues() },
      totalRowsInSection,
      dndType: ALLOCATION_DND_TYPES.TREATMENT,
      onDrop,
    });

  return (
    <>
      <div ref={drop} style={{ display: "contents" }}>
        <figure
          ref={drag}
          className="drag-handle"
          style={{
            cursor: "grab",
            opacity: isDragging ? 0.5 : 1,
            position: "relative",
          }}
          aria-label="Drag to reorder"
        >
          {showDropIndicator && (
            <DropIndicator showBelow={showIndicatorBelow} />
          )}
          ⋮⋮
        </figure>
        <Checkbox
          name={`exposure-mitigation-${row.id}`}
          label=""
          checked={row.has_exposure}
          required={false}
          disabled={isAnyRowUpdating}
          onChange={(e) =>
            handleExposureMitigationChange(e.target.checked, "has_exposure")
          }
        />
        <Checkbox
          name={`exposure-mitigation-${row.id}`}
          label=""
          checked={row.has_mitigation}
          required={false}
          disabled={isAnyRowUpdating}
          onChange={(e) =>
            handleExposureMitigationChange(e.target.checked, "has_mitigation")
          }
        />
        {row.is_excluded && (
          <Checkbox
            name={`non-medicare-${row.id}`}
            label=""
            checked={row.non_medicare}
            required={false}
            disabled={isAnyRowUpdating}
            onChange={(e) =>
              handleExposureMitigationChange(e.target.checked, "non_medicare")
            }
          />
        )}

        {!row.is_excluded && row.showSeedCheckbox && (
          <Checkbox
            name={`seed-payment-${row.id}`}
            label=""
            checked={row.is_included_in_seed_payment}
            required={false}
            disabled={isAnyRowUpdating}
            onChange={(e) => handleSeedCheckboxChange(e.target.checked)}
          />
        )}

        <ControlledInput
          control={control}
          name="name"
          errors={errors}
          onSave={(value) => handleFieldSave("name", value)}
          componentType={TextInput}
          maxLength={TREATMENT_ALLOCATION_NAME_MAX_LENGTH}
          classNames="input--left-align"
        />
        <figure className="cpt-code-display">
          {row.surgical_pricing_calculation ? (
            row.cpt_codes?.length > 0 ? (
              <Tooltip
                content={row.cpt_codes
                  .map(
                    (code) =>
                      `[${code.code}] - ${code.description || "No description"} ($${code.price})`,
                  )
                  .join(" • ")}
              >
                <TextInput
                  name={`cpt_code_display_${row.id}`}
                  value={row.cpt_codes.map((code) => code.code).join(", ")}
                  readOnly={true}
                  className="input--center-align"
                />
              </Tooltip>
            ) : (
              <TextInput
                name={`cpt_code_display_${row.id}`}
                value="-"
                readOnly={true}
                className="input--center-align"
              />
            )
          ) : (
            <Select
              name={`cpt_code_select_${row.id}`}
              value={currentCPTCodeOption}
              inputValue={cptSearchTerm}
              tooltip={
                currentCPTCode
                  ? `${currentCPTCode.code} - ${
                      currentCPTCode.description || "No description"
                    }`
                  : undefined
              }
              placeholder="Select CPT code"
              classNames="input--center-align"
              isClearable={true}
              options={cptCodeOptions}
              formatOptionLabel={formatCPTCodeOptionLabel}
              noOptionsMessage={({ inputValue }) =>
                !inputValue
                  ? "Begin typing a CPT code..."
                  : isSearchingCptCodes
                    ? "Searching..."
                    : "No matches found."
              }
              onInputChange={(value: string) => setCptSearchTerm(value)}
              onMenuOpen={() => setCptSearchTerm("")}
              onChange={(option: CPTCodeOption | null) => {
                handleCPTCodeSelect(option);
              }}
            />
          )}
        </figure>
        <ControlledInput
          control={control}
          name="unit_price"
          errors={errors}
          onSave={(value) => handleFieldSave("unit_price", value)}
          componentType={MaskedInput}
          mask="dollar"
          readOnly={!!row.surgical_pricing_calculation}
        />
        <ControlledInput
          control={control}
          name="units_per_year"
          mask="number"
          errors={errors}
          onSave={(value) => handleFieldSave("units_per_year", value)}
          componentType={MaskedInput}
          maxLength={4}
        />
        <ControlledInput
          control={control}
          name="annual_cost"
          mask="dollar"
          errors={errors}
          readOnly={true}
          onSave={(value) => handleFieldSave("annual_cost", value)}
          componentType={MaskedInput}
        />
        <ControlledInput
          control={control}
          name="number_of_years"
          mask="number"
          errors={errors}
          onSave={(value) => handleFieldSave("number_of_years", value)}
          componentType={MaskedInput}
          maxLength={2}
        />
        {row.is_excluded ? (
          <TotalWithProbability<TreatmentAllocationRowProps>
            control={control}
            errors={errors}
            handleFieldSave={handleFieldSave}
            probability={row.probability_of_success}
          />
        ) : (
          <ControlledInput
            control={control}
            name="total_cost"
            mask="total_dollar"
            errors={errors}
            readOnly={true}
            onSave={(value) => handleFieldSave("total_cost", value)}
            componentType={MaskedInput}
          />
        )}
        <figure className="data-values-btn flex">
          <Tooltip content="Clone row">
            <ButtonIcon
              size="small"
              className="data-value page-view flex as-c"
              onClick={() => row?.onCloneRow?.(row)}
            >
              content_copy
            </ButtonIcon>
          </Tooltip>
        </figure>
        <figure className="data-values-btn flex">
          <Tooltip content="Delete row">
            <ButtonIcon
              size="small"
              className="data-value delete flex as-c"
              onClick={() => row.onSetRowToDelete?.(row)}
            >
              delete
            </ButtonIcon>
          </Tooltip>
        </figure>
        {row.surgical_pricing_calculation && row.onShowSurgicalPricing && (
          <figure className="data-values-btn flex">
            <Tooltip content="View surgical pricing">
              <ButtonIcon
                size="small"
                className="data-value page-view flex as-c"
                onClick={() =>
                  row.onShowSurgicalPricing?.(row.surgical_pricing_calculation!)
                }
              >
                preview
              </ButtonIcon>
            </Tooltip>
          </figure>
        )}
        <div
          className={`exposure-mitigation-wrapper ${!row.is_excluded && row.showSeedCheckbox ? "w-seed" : ""} ${row.is_excluded ? "excluded" : "included-row"}`}
        >
          {row.has_exposure && !row.is_excluded && (
            <div className="exposure-mitigation-wrapper-sub-grid">
              <figure className="exposure-adjustment">
                Exposure Adjustments
                <span className="material-icons">arrow_right_alt</span>
              </figure>

              <ControlledInput
                control={control}
                name="exposed_unit_price"
                errors={errors}
                onSave={(value) => handleFieldSave("exposed_unit_price", value)}
                componentType={MaskedInput}
                mask="dollar"
              />
              <ControlledInput
                control={control}
                name="exposed_units_per_year"
                mask="number"
                errors={errors}
                onSave={(value) =>
                  handleFieldSave("exposed_units_per_year", value)
                }
                componentType={MaskedInput}
                maxLength={4}
              />
              <ControlledInput
                control={control}
                name="exposed_annual_cost"
                mask="dollar"
                errors={errors}
                readOnly={true}
                onSave={(value) =>
                  handleFieldSave("exposed_annual_cost", value)
                }
                componentType={MaskedInput}
              />
              <ControlledInput
                control={control}
                name="number_of_years_exposed"
                mask="number"
                errors={errors}
                onSave={(value) =>
                  handleFieldSave("number_of_years_exposed", value)
                }
                componentType={MaskedInput}
                maxLength={2}
              />
              <ControlledInput
                control={control}
                name="total_exposure"
                mask="total_dollar"
                errors={errors}
                readOnly={true}
                onSave={(value) => handleFieldSave("total_exposure", value)}
                componentType={MaskedInput}
              />
              <figure></figure>
            </div>
          )}
          {row.has_mitigation && (
            <div className="exposure-mitigation-wrapper-sub-grid">
              <figure className="exposure-adjustment">
                Mitigation Adjustments
                <span className="material-icons">arrow_right_alt</span>
              </figure>

              <ControlledInput
                control={control}
                name="mitigated_unit_price"
                errors={errors}
                onSave={(value) =>
                  handleFieldSave("mitigated_unit_price", value)
                }
                componentType={MaskedInput}
                mask="dollar"
              />
              <ControlledInput
                control={control}
                name="mitigated_units_per_year"
                mask="number"
                errors={errors}
                onSave={(value) =>
                  handleFieldSave("mitigated_units_per_year", value)
                }
                componentType={MaskedInput}
                maxLength={4}
              />
              <ControlledInput
                control={control}
                name="mitigated_annual_cost"
                mask="dollar"
                errors={errors}
                readOnly={true}
                onSave={(value) =>
                  handleFieldSave("mitigated_annual_cost", value)
                }
                componentType={MaskedInput}
              />
              <ControlledInput
                control={control}
                name="number_of_years_mitigated"
                mask="number"
                errors={errors}
                onSave={(value) =>
                  handleFieldSave("number_of_years_mitigated", value)
                }
                componentType={MaskedInput}
                maxLength={2}
              />
              <ControlledInput
                control={control}
                name="total_mitigation"
                mask="total_dollar"
                errors={errors}
                readOnly={true}
                onSave={(value) => handleFieldSave("total_mitigation", value)}
                componentType={MaskedInput}
              />
              <figure></figure>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
